import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { KeyRound, Loader2, CheckCircle2, PlugZap, Save } from "lucide-react";

export default function Settings() {
  const { refreshUser } = useAuth();
  const [subKey, setSubKey] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [info, setInfo] = useState({ configured: false, subscription_key_masked: "", username: "" });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get("/settings/cargus").then((r) => {
      setInfo(r.data);
      setUsername(r.data.username || "");
    });
  }, []);

  const save = async () => {
    if (!subKey || !username || !password) {
      toast.error("Completează toate câmpurile");
      return;
    }
    setSaving(true);
    try {
      await api.put("/settings/cargus", { subscription_key: subKey, username, password });
      await refreshUser();
      const r = await api.get("/settings/cargus");
      setInfo(r.data);
      toast.success("Credențiale salvate");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    if (!subKey || !username || !password) {
      toast.error("Completează toate câmpurile pentru test");
      return;
    }
    setTesting(true);
    try {
      const { data } = await api.post("/settings/cargus/test", {
        subscription_key: subKey,
        username,
        password,
      });
      toast.success(data.message);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setTesting(false);
    }
  };

  const field =
    "w-full bg-card border border-border rounded-sm px-3 py-2.5 text-sm font-mono outline-none focus:border-primary transition-colors";

  return (
    <Layout>
      <div className="p-8 max-w-2xl mx-auto">
        <h1 className="text-3xl font-semibold tracking-tight">Setări</h1>
        <p className="text-sm text-muted-foreground mt-1 mb-8">
          Credențialele Urgent Cargus API v3 (portal developer Cargus).
        </p>

        {info.configured && (
          <div className="mb-6 flex items-center gap-2 border border-[#34C759]/30 bg-[#34C759]/10 rounded-sm px-4 py-3 text-sm text-[#34C759]">
            <CheckCircle2 size={16} />
            Credențiale configurate ({info.subscription_key_masked})
          </div>
        )}

        <div className="border border-border rounded-sm bg-card p-6 space-y-4">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <KeyRound size={16} /> Credențiale Cargus
          </div>

          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider">Subscription Key</label>
            <input
              data-testid="sub-key-input"
              value={subKey}
              onChange={(e) => setSubKey(e.target.value)}
              placeholder="Ocp-Apim-Subscription-Key"
              className={`${field} mt-1.5`}
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider">Utilizator</label>
            <input
              data-testid="cargus-user-input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Utilizator WebExpress"
              className={`${field} mt-1.5`}
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground uppercase tracking-wider">Parolă</label>
            <input
              data-testid="cargus-pass-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Parolă cont Cargus"
              className={`${field} mt-1.5`}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              data-testid="test-cargus-btn"
              onClick={test}
              disabled={testing}
              className="flex items-center gap-2 border border-border rounded-sm px-4 py-2.5 text-sm hover:bg-muted transition-colors disabled:opacity-50"
            >
              {testing ? <Loader2 size={15} className="animate-spin" /> : <PlugZap size={15} />}
              Testează conexiunea
            </button>
            <button
              data-testid="save-cargus-btn"
              onClick={save}
              disabled={saving}
              className="flex items-center gap-2 bg-primary text-primary-foreground rounded-sm px-4 py-2.5 text-sm font-medium hover:bg-white/90 transition-colors disabled:opacity-50"
            >
              {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
              Salvează
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
